import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, PenLine, X, Loader2, Send, ImagePlus, Award } from "lucide-react";
import { Reveal, MaskReveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", city: "", rating: 0, text: "", company: "" };
const field =
  "bg-black border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37] focus-visible:ring-2 focus-visible:border-[#D4AF37] h-12 rounded-lg";

const Stars = ({ n, size = 15 }) => (
  <div className="flex gap-1">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} className={i < n ? "text-[#D4AF37] fill-[#D4AF37]" : "text-neutral-700"} />
    ))}
  </div>
);

const Avatar = ({ name }) => (
  <div className="h-11 w-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-serif-display text-lg shrink-0">
    {(name || "?").trim().charAt(0).toUpperCase()}
  </div>
);

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`, { timeout: 15000 });
      setReviews(Array.isArray(data) ? data : []);
    } catch (e) {
      setReviews([]);
    }
  };
  useEffect(() => { load(); }, []);

  // Deep-link: opening the site with ?review=1 (or #leave-review) auto-scrolls
  // to this section and opens the review form. Used by the review QR code.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsReview = params.get("review") === "1" || window.location.hash === "#leave-review";
    if (!wantsReview) return;
    const t = setTimeout(() => {
      document.getElementById("testimonials")?.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(true);
    }, 700);
    return () => clearTimeout(t);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const close = () => { setOpen(false); setForm(EMPTY); setHover(0); setPhoto(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Please select a star rating.");
    if (!form.name || !form.text) return toast.error("Please add your name and a short review.");
    setSubmitting(true);
    try {
      let photo_id = "";
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const up = await axios.post(`${API}/reviews/photo`, fd, { timeout: 30000 });
        photo_id = up.data.photo_id;
      }
      await axios.post(`${API}/reviews`, { ...form, photo_id }, { timeout: 15000 });
      toast.success("Thank you! Your review was submitted and will appear once approved.");
      close();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong submitting your review. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const count = reviews.length;
  const avg = count ? reviews.reduce((s, r) => s + (r.rating || 0), 0) / count : 0;
  const featured = reviews.find((r) => r.featured);
  const grid = reviews.filter((r) => !r.featured);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" /> Kind Words <span className="h-px w-10 bg-[#D4AF37]" />
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.03]">
            <MaskReveal lines={["Loved by our", "neighbors."]} />
          </h2>
          {count > 0 && (
            <div className="flex items-center justify-center gap-2 mt-6" data-testid="reviews-summary">
              <Stars n={Math.round(avg)} size={18} />
              <span className="text-white font-semibold text-lg">{avg.toFixed(1)}</span>
              <span className="text-neutral-600">·</span>
              <span className="text-neutral-500 text-sm">{count} review{count > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>

        {featured && (
          <Reveal className="mb-6" data-testid="featured-review">
            <div className="relative rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#161311] to-[#0d0d0d] p-8 md:p-10 overflow-hidden">
              <div className="absolute top-5 right-5 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-black bg-[#D4AF37] rounded-full px-3 py-1 font-bold">
                <Award size={13} /> Featured
              </div>
              <div className="flex flex-col md:flex-row gap-7 items-start">
                {featured.photo_id && (
                  <img
                    src={`${API}/reviews/photo/${featured.photo_id}`}
                    alt={`Photo from ${featured.name}`}
                    data-testid="featured-photo"
                    className="w-full md:w-56 h-48 object-cover rounded-xl border border-[#D4AF37]/20 shrink-0"
                  />
                )}
                <div className="flex-1">
                  <Stars n={featured.rating} size={18} />
                  <p className="text-neutral-100 mt-4 text-xl md:text-2xl font-serif-display italic leading-relaxed">"{featured.text}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    <Avatar name={featured.name} />
                    <div>
                      <div className="text-white font-medium">{featured.name}</div>
                      {featured.city ? <div className="text-neutral-500 text-xs">{featured.city}</div> : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {grid.length > 0 ? (
          <div className={`grid grid-cols-1 gap-5 ${grid.length === 1 ? "md:grid-cols-1 max-w-md mx-auto" : grid.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`} data-testid="reviews-grid">
            {grid.slice(0, 9).map((t, i) => (
              <Reveal
                key={t.id || i}
                delay={(i % 3) * 0.1}
                data-testid={`review-card-${i}`}
                className="rounded-2xl border border-white/10 bg-[#111111] p-8 flex flex-col hover:border-[#D4AF37]/30 transition-colors duration-400"
              >
                <Quote size={30} className="text-[#D4AF37]/40" />
                <div className="mt-4"><Stars n={t.rating} /></div>
                {t.photo_id && (
                  <img
                    src={`${API}/reviews/photo/${t.photo_id}`}
                    alt={`Photo from ${t.name}`}
                    className="mt-4 rounded-lg w-full h-40 object-cover border border-white/10"
                  />
                )}
                <p className="text-neutral-300 mt-4 leading-relaxed flex-1 italic">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                  <Avatar name={t.name} />
                  <div>
                    <div className="text-white font-medium text-sm">{t.name}</div>
                    {t.city ? <div className="text-neutral-500 text-xs">{t.city}</div> : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          !featured && (
            <Reveal className="text-center rounded-2xl border border-white/10 bg-[#111111] py-16 px-6" data-testid="reviews-empty">
              <Quote size={36} className="text-[#D4AF37]/40 mx-auto" />
              <p className="text-neutral-300 mt-4 text-lg font-serif-display">Be the first to share your ride experience.</p>
              <p className="text-neutral-500 mt-2 text-sm">Your review helps neighbors ride with confidence.</p>
            </Reveal>
          )
        )}

        <div className="text-center mt-10">
          <button
            data-testid="write-review-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-3 rounded-full bg-[#D4AF37] text-black px-8 py-4 font-semibold hover:bg-[#f3e5ab] transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
          >
            <PenLine size={18} /> Write a Review
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="review-modal"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ scale: 0.94, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.96, y: 10, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-lg rounded-2xl border border-[#D4AF37]/30 bg-[#0d0d0d] p-7 md:p-9 max-h-[90vh] overflow-y-auto"
            >
              <button onClick={close} data-testid="review-modal-close" className="absolute top-4 right-4 text-neutral-400 hover:text-white">
                <X size={22} />
              </button>
              <h3 className="font-serif-display text-3xl text-white">Write a Review</h3>
              <p className="text-neutral-500 text-sm mt-1">Share your experience — it appears after Rui approves it.</p>

              <form onSubmit={submit} data-testid="review-form" noValidate className="mt-6 space-y-5">
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                <div>
                  <Label className="text-neutral-300 text-sm">Your Rating</Label>
                  <div className="flex gap-2 mt-2" data-testid="review-rating">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`review-star-${s}`}
                        onClick={() => setForm((f) => ({ ...f, rating: s }))}
                        onMouseEnter={() => setHover(s)}
                        onMouseLeave={() => setHover(0)}
                        className="transition-transform duration-150 hover:scale-110"
                      >
                        <Star size={30} className={(hover || form.rating) >= s ? "text-[#D4AF37] fill-[#D4AF37]" : "text-neutral-700"} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Name</Label>
                    <Input data-testid="review-name" value={form.name} onChange={set("name")} placeholder="Your name" className={field} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">City / Town <span className="text-neutral-600">(optional)</span></Label>
                    <Input data-testid="review-city" value={form.city} onChange={set("city")} placeholder="Haymarket, VA" className={field} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 text-sm">Your Review</Label>
                  <Textarea
                    data-testid="review-text"
                    value={form.text}
                    onChange={set("text")}
                    placeholder="Tell us about your ride with KAT Transportation..."
                    className="bg-black border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37] focus-visible:ring-2 focus-visible:border-[#D4AF37] rounded-lg min-h-[120px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 text-sm">Add a Photo <span className="text-neutral-600">(optional)</span></Label>
                  <label
                    htmlFor="review-photo-input"
                    data-testid="review-photo-label"
                    className="flex items-center gap-3 rounded-lg border border-dashed border-white/20 bg-black px-4 py-3 cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
                  >
                    <ImagePlus size={18} className="text-[#D4AF37]" />
                    <span className="text-neutral-400 text-sm truncate">{photo ? photo.name : "Choose an image (JPG, PNG, WEBP · max 6MB)"}</span>
                  </label>
                  <input
                    id="review-photo-input"
                    data-testid="review-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && f.size > 6 * 1024 * 1024) { toast.error("That image is too large (max 6MB)."); return; }
                      setPhoto(f || null);
                    }}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="review-submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-8 py-4 font-semibold hover:bg-[#f3e5ab] transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
